import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { X } from 'lucide-react';

// USGA/WHS Handicap Index — simplified. Supports 18-hole OR 9-hole scores per round
// plus optional Playing Condition Adjustment (-1 to +3).
const EMPTY_ROW = { rating: '', slope: '', score18: '', score9: '', pca: '' };

export default function GolfHandicapCalc() {
  const [mode, setMode] = useState('index');
  const [rows, setRows] = useState(() => {
    const seed = [
      { rating: 70.3, slope: 126, score18: 85, score9: '', pca: '' },
      { rating: 35.3, slope: 122, score18: '',  score9: 41, pca: '' },
      { rating: 66.8, slope: 120, score18: 82, score9: '', pca: '' },
      { rating: 65.9, slope: 118, score18: 85, score9: '', pca: '' },
      { rating: 74.3, slope: 137, score18: 88, score9: '', pca: '' },
      { rating: 71.1, slope: 126, score18: 90, score9: '', pca: '' },
    ];
    // Pad to 20 rows to match Calculator.net's layout
    while (seed.length < 20) seed.push({ ...EMPTY_ROW });
    return seed;
  });
  const [idx, setIdx] = useState(15.4);
  const [rating, setRating] = useState(72.1);
  const [slope, setSlope] = useState(130);
  const [par, setPar] = useState(72);

  const differentials = useMemo(() => rows.map(r => {
    const rt = Number(r.rating), sl = Number(r.slope);
    const s18 = r.score18 !== '' ? Number(r.score18) : null;
    const s9 = r.score9 !== '' ? Number(r.score9) : null;
    const pca = r.pca !== '' ? Number(r.pca) : 0;
    if (!rt || !sl) return null;
    if (s18 != null && !isNaN(s18)) {
      return (((s18 - rt) * 113) / sl) - pca;
    }
    if (s9 != null && !isNaN(s9)) {
      // Approximate: scale 9-hole diff to 18-hole equivalent
      const nine = ((s9 - rt) * 113) / sl;
      return (nine * 2) - pca;
    }
    return null;
  }), [rows]);

  const handicapIndex = useMemo(() => {
    const arr = differentials.filter(d => d != null).sort((a, b) => a - b);
    const n = arr.length;
    if (n < 3) return null; // Need at least 54 holes = 3 rounds
    let take = 1;
    if (n >= 5) take = 1;
    if (n >= 6) take = 2;
    if (n >= 8) take = 2;
    if (n >= 9) take = 3;
    if (n >= 12) take = 4;
    if (n >= 15) take = 5;
    if (n >= 17) take = 6;
    if (n >= 19) take = 7;
    if (n >= 20) take = 8;
    take = Math.min(take, n);
    const avg = arr.slice(0, take).reduce((a, b) => a + b, 0) / take;
    return (avg * 0.96);
  }, [differentials]);

  const courseHandicap = ((Number(idx) * Number(slope) / 113) + (Number(rating) - Number(par)));

  const update = (i, k, v) => { const nr = [...rows]; nr[i] = { ...nr[i], [k]: v }; setRows(nr); };
  const clearRow = (i) => update(i, 'rating', '') || setRows(rows.map((r, ix) => ix === i ? { ...EMPTY_ROW } : r));
  const addRow = () => setRows([...rows, { ...EMPTY_ROW }]);

  return (
    <CalcShell look="cnet" title="Golf Handicap Calculator" subtitle="Compute your Handicap Index from recent rounds (18-hole or 9-hole), or convert an Index to a Course Handicap.">
      <div className="flex gap-2 mb-0">
        {[['index','Index of Handicap Calculator'],['course','Course Handicap Calculator']].map(([v,l]) => (
          <button key={v} data-testid={`golf-tab-${v}`}
            className={`cnet-tab ${mode === v ? 'active' : ''}`}
            onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="cnet-tab-body">
      {mode === 'index' ? (
        <div>
          <p className="text-sm text-slate-700 mb-3">Use this calculator to compute the index of handicap for a golfer given data from at least 54 holes (3 rounds of 18-holes) of playing data. When filling the form, please provide either an 18-hole or 9-hole score. Do not provide both. The playing condition adjustment is an optional value between −1 and 3. If left blank, it will be treated as 0.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="golf-index-table">
              <thead>
                <tr className="text-center text-xs">
                  <td className="p-2 bg-slate-200"></td>
                  <td className="p-2 bg-blue-100 font-bold">Rating of<br/>Course</td>
                  <td className="p-2 bg-green-100 font-bold">Rating of<br/>Course Slope</td>
                  <td className="p-2 bg-orange-100 font-bold">18-hole Score</td>
                  <td className="p-2 bg-pink-100 font-bold">9-hole Score</td>
                  <td className="p-2 bg-yellow-100 font-bold">Playing<br/>Condition<br/>Adjustment</td>
                  <td className="p-2 bg-slate-200 w-8"></td>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1 bg-slate-100 text-sm">Round #{i + 1}</td>
                    <td className="p-1 bg-blue-50"><input data-testid={`golf-r${i}-rating`} className="w-full px-2 py-1 border border-slate-300 rounded text-center" type="number" step="0.1" value={r.rating} onChange={e => update(i, 'rating', e.target.value)} /></td>
                    <td className="p-1 bg-green-50"><input data-testid={`golf-r${i}-slope`} className="w-full px-2 py-1 border border-slate-300 rounded text-center" type="number" value={r.slope} onChange={e => update(i, 'slope', e.target.value)} /></td>
                    <td className="p-1 bg-orange-50"><input data-testid={`golf-r${i}-s18`} className="w-full px-2 py-1 border border-slate-300 rounded text-center" type="number" value={r.score18} onChange={e => update(i, 'score18', e.target.value)} /></td>
                    <td className="p-1 bg-pink-50"><input data-testid={`golf-r${i}-s9`} className="w-full px-2 py-1 border border-slate-300 rounded text-center" type="number" value={r.score9} onChange={e => update(i, 'score9', e.target.value)} /></td>
                    <td className="p-1 bg-yellow-50"><input data-testid={`golf-r${i}-pca`} className="w-full px-2 py-1 border border-slate-300 rounded text-center" type="number" step="0.1" min="-1" max="3" value={r.pca} onChange={e => update(i, 'pca', e.target.value)} /></td>
                    <td className="p-1 bg-slate-100 text-center"><button onClick={() => clearRow(i)} data-testid={`golf-r${i}-x`} className="w-6 h-6 rounded bg-red-500 text-white hover:bg-red-600 inline-flex items-center justify-center"><X className="w-3 h-3" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={addRow} className="mt-3 px-3 py-1 border rounded text-sm">+ Add Round</button>

          <div className="mt-4 p-5 rounded-xl bg-green-50 border border-green-200 text-center">
            <div className="text-sm text-slate-600">Handicap Index (WHS, best-N × 0.96)</div>
            <div className="text-4xl font-bold text-green-700" data-testid="handicap-index">{handicapIndex != null ? handicapIndex.toFixed(1) : '—'}</div>
            {handicapIndex == null && <div className="text-xs text-red-600 mt-1">Provide at least 3 valid rounds (18-hole or 9-hole) to compute.</div>}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Handicap Index</label><input type="number" step="0.1" value={idx} onChange={e => setIdx(e.target.value)} data-testid="golf-course-idx" /></div>
            <div className="field"><label>Course Rating</label><input type="number" step="0.1" value={rating} onChange={e => setRating(e.target.value)} data-testid="golf-course-rating" /></div>
            <div className="field"><label>Slope Rating</label><input type="number" value={slope} onChange={e => setSlope(e.target.value)} data-testid="golf-course-slope" /></div>
            <div className="field"><label>Par</label><input type="number" value={par} onChange={e => setPar(e.target.value)} data-testid="golf-course-par" /></div>
          </div>
          <div>
            <div className="p-5 rounded-xl bg-green-50 border border-green-200 text-center">
              <div className="text-sm text-slate-600">Course Handicap</div>
              <div className="text-4xl font-bold text-green-700" data-testid="course-handicap">{courseHandicap.toFixed(1)}</div>
              <div className="text-xs text-slate-600 mt-2">Formula: (Index × Slope / 113) + (Course Rating − Par)</div>
            </div>
          </div>
        </div>
      )}
      </div>
    </CalcShell>
  );
}
